import React, { useState, useContext, useEffect, useCallback } from "react";
import { FirebaseContext } from "../../../context/Firebase";
import { DataContext } from "../../../context/Data";
import { AuthContext } from "../../../context/Authorization";
import Toast from "../../../components/Toast/Toast";
import NoRouteV2 from "../../../components/StatusPages/NoRouteV2";
import {
  AiOutlineLoading3Quarters,
  AiOutlinePlus,
  AiOutlineMail,
  AiOutlineDelete,
  AiOutlineEye,
  AiOutlineEyeInvisible,
  AiOutlineCheckCircle,
} from "react-icons/ai";

const emptyForm = { first: "", last: "", email: "", password: "" };

// Mirrors the server-side check in createAccountUser.js — never trust client-only validation for
// a security-relevant minimum.
const PASSWORD_MIN_LENGTH = 8;
const passwordCriteria = (password) => ({
  length: password.length >= PASSWORD_MIN_LENGTH,
  letter: /[a-zA-Z]/.test(password),
  number: /[0-9]/.test(password),
});
const isPasswordValid = (password) => Object.values(passwordCriteria(password)).every(Boolean);

const PASSWORD_CHECKLIST = [
  { key: "length", label: `At least ${PASSWORD_MIN_LENGTH} characters` },
  { key: "letter", label: "At least one letter" },
  { key: "number", label: "At least one number" },
];

const AuditCell = ({ name, date }) => (
  <div className="text-sm">
    <div className="text-gray-900">{name || "—"}</div>
    {date && <div className="text-xs text-gray-500">{date}</div>}
  </div>
);

const UserAdministration = () => {
  const { functions, auth } = useContext(FirebaseContext);
  const { account } = useContext(DataContext);
  const { useAuth, authData } = useContext(AuthContext);
  const { userHasPermission } = useAuth();
  const currentUserId = auth.currentUser?.uid;

  // This screen never grants permissions (not even Account Admin) — it only provisions/removes
  // brand-new logins, so access is restricted to the account's User Admin permission or
  // GLOBAL_ADMIN, mirroring the backend check in createAccountUser.js / updateUser.js.
  const canManageUsers =
    authData?.user?.protected?.roles?.includes("GLOBAL_ADMIN") || userHasPermission(`${account}_ACCT_USERS_ADMIN`);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [toast, setToast] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [confirmAction, setConfirmAction] = useState(null); // { type: "resend" | "remove", user }
  const [actionLoading, setActionLoading] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadUsers = useCallback(async () => {
    if (!account) return;

    setLoadingUsers(true);
    try {
      const result = await functions.getAccountUsers({ account_eid: account });
      setUsers(result.users);
    } catch (error) {
      console.error("Load users error:", error);
      showToast(error.message || "Failed to load users", "error");
    } finally {
      setLoadingUsers(false);
    }
  }, [functions, account]);

  useEffect(() => {
    if (canManageUsers) loadUsers();
  }, [loadUsers, canManageUsers]);

  if (!canManageUsers) {
    return <NoRouteV2 />;
  }

  const onFormChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const validateForm = () => {
    const valErrors = {};

    if (!form.first.trim()) valErrors.first = "First name is required.";
    if (!form.last.trim()) valErrors.last = "Last name is required.";

    if (!form.email.trim()) {
      valErrors.email = "Email is required.";
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      valErrors.email = "Invalid email.";
    }

    if (!isPasswordValid(form.password)) {
      valErrors.password = "Password does not meet the minimum requirements.";
    }

    setErrors(valErrors);
    return Object.keys(valErrors).length === 0;
  };

  const openCreateModal = () => {
    setForm(emptyForm);
    setErrors({});
    setShowPassword(false);
    setShowCreateModal(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setCreating(true);
    try {
      // Brand-new users only — backend rejects an already-existing email with a generic
      // "contact support" error rather than saying why. Only flat fields go over the wire; the
      // backend builds `protected` itself and never grants any permission through this flow.
      await functions.createAccountUser({
        email: form.email,
        first_name: form.first,
        last_name: form.last,
        password: form.password,
        account_eid: account,
      });
      showToast("User added successfully!", "success");
      setShowCreateModal(false);
      await loadUsers();
    } catch (error) {
      console.error("Create user error:", error);
      showToast(error.message || "Failed to create user", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleResendPasswordEmail = async (user) => {
    setActionLoading(true);
    try {
      await auth.doPasswordReset(user.email);
      showToast("Password reset email sent.", "success");
    } catch (error) {
      console.error("Password reset email error:", error);
      showToast(error.message || "Failed to send password reset email", "error");
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleRemoveUser = async (user) => {
    setActionLoading(true);
    try {
      // Backend decides what "remove" means: drop just this account and repoint
      // default_account, or fully disable if this was the user's only account.
      await functions.updateUser({ uid: user.id, account_eid: account });
      showToast("User removed from this account.", "success");
      await loadUsers();
    } catch (error) {
      console.error("Remove user error:", error);
      showToast(error.message || "Failed to remove user", "error");
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const onConfirmAction = () => {
    if (!confirmAction) return;
    if (confirmAction.type === "resend") return handleResendPasswordEmail(confirmAction.user);
    return handleRemoveUser(confirmAction.user);
  };

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <h1 className="text-3xl font-bold text-gray-900">User Administration</h1>
        <p className="text-sm text-gray-600 mt-2">Create and manage users for this account</p>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Users</h2>
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-gray-800 hover:bg-gray-700 text-white"
                >
                  <AiOutlinePlus className="h-4 w-4" />
                  Add User
                </button>
              </div>

              {loadingUsers ? (
                <div className="flex items-center justify-center py-10 text-gray-500">
                  <AiOutlineLoading3Quarters className="h-5 w-5 mr-2 animate-spin" />
                  Loading users...
                </div>
              ) : users.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">No users found for this account.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Updated
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          &nbsp;
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                            {user.first_name} {user.last_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{user.email}</td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                user.disabled ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                              }`}
                            >
                              {user.disabled ? "Disabled" : "Active"}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <AuditCell name={user.createdByName} date={user.createdAt} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <AuditCell name={user.updatedByName} date={user.updatedAt} />
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => setConfirmAction({ type: "resend", user })}
                              disabled={user.disabled}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-500"
                              title="Resend password email"
                            >
                              <AiOutlineMail className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmAction({ type: "remove", user })}
                              disabled={user.id === currentUserId || user.disabled}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-500"
                              title={user.id === currentUserId ? "You cannot remove yourself" : "Remove user"}
                            >
                              <AiOutlineDelete className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => !creating && setShowCreateModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add User</h3>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={form.first}
                    onChange={onFormChange("first")}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.first ? "border-red-400" : "border-gray-300"
                    }`}
                    placeholder="First name"
                  />
                  {errors.first && <p className="mt-1 text-xs text-red-600">{errors.first}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={form.last}
                    onChange={onFormChange("last")}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.last ? "border-red-400" : "border-gray-300"
                    }`}
                    placeholder="Last name"
                  />
                  {errors.last && <p className="mt-1 text-xs text-red-600">{errors.last}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={onFormChange("email")}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? "border-red-400" : "border-gray-300"
                    }`}
                    placeholder="user@example.com"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={onFormChange("password")}
                      className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.password ? "border-red-400" : "border-gray-300"
                      }`}
                      placeholder="Create a password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible className="h-4 w-4" />
                      ) : (
                        <AiOutlineEye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
                  <ul className="mt-2 space-y-1">
                    {PASSWORD_CHECKLIST.map(({ key, label }) => {
                      const met = passwordCriteria(form.password)[key];
                      return (
                        <li
                          key={key}
                          className={`text-xs flex items-center gap-1.5 ${met ? "text-green-600" : "text-gray-400"}`}
                        >
                          {met ? (
                            <AiOutlineCheckCircle className="h-3.5 w-3.5" />
                          ) : (
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-300 inline-block ml-0.5 mr-0.5" />
                          )}
                          {label}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className={`px-4 py-2 text-sm font-medium rounded-md text-white ${
                    creating ? "bg-gray-400 cursor-not-allowed" : "bg-gray-800 hover:bg-gray-700"
                  }`}
                >
                  {creating ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm resend / remove */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => !actionLoading && setConfirmAction(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {confirmAction.type === "resend" ? "Resend password email?" : "Remove user?"}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {confirmAction.type === "resend"
                ? `Send a password reset email to ${confirmAction.user.email}?`
                : `Remove ${confirmAction.user.email} from this account? This can't be undone without contacting support.`}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirmAction}
                disabled={actionLoading}
                className={`px-4 py-2 text-sm font-medium rounded-md text-white ${
                  confirmAction.type === "remove"
                    ? actionLoading
                      ? "bg-red-300 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                    : actionLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gray-800 hover:bg-gray-700"
                }`}
              >
                {actionLoading ? "Working..." : confirmAction.type === "resend" ? "Send" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAdministration;
